/**
 * Pure helpers behind «صدور فاکتور» (issue an invoice from delivered order lines).
 *
 * Quantities are exact rationals (`"3"` or `"3/2"`) — the same wire format the
 * API accepts — and money is integer-Rial `bigint`; nothing here touches a JS
 * `number`. The totals are a preview: the server recomputes and is authoritative.
 */
import type { InvoiceDraftAllocation } from '../../lib/types/crm.js';

export interface Rational {
  readonly num: bigint;
  readonly den: bigint;
}

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x === 0n ? 1n : x;
}

function reduce(value: Rational): Rational {
  const g = gcd(value.num, value.den);
  return { num: value.num / g, den: value.den / g };
}

const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';

function toAscii(text: string): string {
  return text
    .replace(/[۰-۹]/g, (d) => String(PERSIAN_DIGITS.indexOf(d)))
    .replace(/[٠-٩]/g, (d) => String(ARABIC_DIGITS.indexOf(d)))
    .replace(/٫/g, '.')
    .replace(/٬|,/g, '')
    .trim();
}

/** Accepts `3`, `3.5`, `3/2` (Persian digits too). Returns null for anything else or ≤ 0. */
export function parseQuantity(input: string): Rational | null {
  const text = toAscii(input);
  const fraction = /^(\d{1,12})\/(\d{1,12})$/.exec(text);
  if (fraction) {
    const den = BigInt(fraction[2]!);
    const num = BigInt(fraction[1]!);
    return num > 0n && den > 0n ? reduce({ num, den }) : null;
  }
  const decimal = /^(\d{1,12})(?:\.(\d{1,4}))?$/.exec(text);
  if (!decimal) return null;
  const part = decimal[2] ?? '';
  const num = BigInt(decimal[1]! + part);
  return num > 0n ? reduce({ num, den: 10n ** BigInt(part.length) }) : null;
}

export function parseRationalText(text: string): Rational {
  const [n, d] = text.split('/');
  return reduce({ num: BigInt(n!), den: BigInt(d ?? '1') });
}

export function rationalText(value: Rational): string {
  const r = reduce(value);
  return r.den === 1n ? r.num.toString() : `${r.num}/${r.den}`;
}

export function compareRational(a: Rational, b: Rational): number {
  const left = a.num * b.den;
  const right = b.num * a.den;
  return left === right ? 0 : left < right ? -1 : 1;
}

function subtract(a: Rational, b: Rational): Rational {
  return reduce({ num: a.num * b.den - b.num * a.den, den: a.den * b.den });
}

export function roundHalfUp(numerator: bigint, denominator: bigint): bigint {
  return (numerator * 2n + denominator) / (denominator * 2n);
}

/** First-in-first-out spread of `quantity` over the delivery movements; null when it exceeds what is available. */
export function allocateFifo(
  allocations: readonly InvoiceDraftAllocation[],
  quantity: Rational,
): { movementId: string; quantity: string }[] | null {
  let left = quantity;
  const out: { movementId: string; quantity: string }[] = [];
  for (const allocation of allocations) {
    if (left.num <= 0n) break;
    const available = parseRationalText(allocation.availableQuantity);
    const take = compareRational(left, available) <= 0 ? left : available;
    out.push({ movementId: allocation.movementId, quantity: rationalText(take) });
    left = subtract(left, take);
  }
  return left.num === 0n ? out : null;
}

export interface IssueLineInput {
  readonly quantity: Rational;
  readonly unitPriceRial: bigint;
  readonly discountRial: bigint;
}

export interface IssueLineAmounts {
  readonly grossRial: bigint;
  readonly taxableRial: bigint;
  readonly vatRial: bigint;
  readonly totalRial: bigint;
  readonly error: string | null;
}

export function issueLineAmounts(
  line: IssueLineInput,
  taxRate: { readonly numerator: bigint; readonly denominator: bigint } | null,
): IssueLineAmounts {
  const gross = roundHalfUp(line.quantity.num * line.unitPriceRial, line.quantity.den);
  if (line.discountRial < 0n || line.discountRial >= gross) {
    return {
      grossRial: gross,
      taxableRial: 0n,
      vatRial: 0n,
      totalRial: 0n,
      error: 'تخفیف باید کمتر از مبلغ ردیف باشد.',
    };
  }
  const taxable = gross - line.discountRial;
  const vat =
    taxRate && taxRate.denominator > 0n
      ? roundHalfUp(taxable * taxRate.numerator, taxRate.denominator)
      : 0n;
  return {
    grossRial: gross,
    taxableRial: taxable,
    vatRial: vat,
    totalRial: taxable + vat,
    error: null,
  };
}

export function sumBig(values: readonly bigint[]): bigint {
  return values.reduce((s, v) => s + v, 0n);
}
